/**
 * Moment 1 action palette (Draft LC Review results).
 *
 * Single action: download the amendment request PDF generated by
 * /api/importer/amendment-request. Fed into VerdictTab via the actionSlot
 * prop from Phase 1/5.
 */

import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAmendmentRequest } from "@/hooks/use-importer-actions";

export interface DraftLcActionsProps {
  sessionId: string;
  lcNumber?: string;
}

export function DraftLcActions({ sessionId, lcNumber }: DraftLcActionsProps) {
  const amendment = useAmendmentRequest();
  const { toast } = useToast();

  const onDownload = async () => {
    try {
      const blob = await amendment.mutateAsync({
        validationSessionId: sessionId,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `amendment-request-${lcNumber ?? sessionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after the download starts so the browser has time to grab it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast({
        title: "Amendment request downloaded",
        description: "Send to your issuing bank to amend the draft LC.",
      });
    } catch (err: any) {
      toast({
        title: "Amendment request failed",
        description: err?.message ?? "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Button
        onClick={onDownload}
        disabled={amendment.isLoading}
        size="lg"
      >
        <Download className="mr-2 h-4 w-4" />
        {amendment.isLoading
          ? "Generating…"
          : "Download Amendment Request"}
      </Button>
    </div>
  );
}
